import { Line } from '../models/shapes/Line';
import { Vector } from '../models/Vector';
import { projectPointToVector, transformPointByMatrix3 } from '../shared/common';
import { a } from "../shared/globalState/a";
import { v4 as uuid } from 'uuid';
import { ObjectTypes } from "../shared/enums";
import { Shape } from '../shared/types';
import { Point } from 'jspdf';
import { Join } from '../models/Join';
import { mat3 } from 'gl-matrix';

/**
 * Что такое "маршрут сети"? Кажется, что это путь до ближайшего разветвления. 
 * А с другой стороны должна быть возможность выделить главную ветвь. В Revit такая задача
 * решается несколькими нажатиями Tab. Сначала выделяется участок до ближайшего соединения,
 * затем этот участок плюс общий участок,
 * затем вся сеть.
 * Это было бы удобно - можно выделить каждую ветвь и посмотреть потери давления.
 */

/**
 * TODO: 
 * - при копировании элементов вновь созданные элементы должны принадлежать другому маршруту
 * - выделение маршрута
 * - перемещение блоков вместе с маршрутом
 * - расчёт сети
 */


/**
 * Обеспечить надёжный механизм соединения и разъединения участков
 */


function observeJoins(mouse: Point, segments: Shape[]):Line | Line[] | null {

    var segs = segments.filter(s => s instanceof Line) as Line[];

    segs.forEach(s => {
        if (s.isinGripStart(mouse)) {
            a.joinedShapeId = s.id;
            // открыть соединение у текущего участка
            s.startJ.active = true;

        }
        else if (s.isinGripEnd(mouse)) {
            a.joinedShapeId = s.id;

            // открыть соединение у текущего участка
            s.endJ.active = true;
        }
        else {

            // закрыть соединения у текущего участка
            s.startJ.active = false;
            s.endJ.active = false;
        }
    });

    var connectedAtStart = segs.find(s => s.startJ.active);
    var connectedAtEnd = segs.find(s => s.endJ.active);

    if (connectedAtStart && connectedAtEnd) {
        return [
            connectedAtStart, connectedAtEnd
        ]
    }
    else if (connectedAtStart && !connectedAtEnd) {
        return connectedAtStart
    }
    else if (!connectedAtStart && connectedAtEnd) {
        return connectedAtEnd
    }

    return null;
}

function connect(shapes: Shape[]) {
    // Find the first line with an active junction point
    const activeLine = shapes
        .filter(s => s instanceof Line)
        .find((l) => (l as Line).startJ.active || (l as Line).endJ.active) as Line | undefined;

    if (!activeLine) {
        return;
    }

    // Generate junction ID for connecting the lines
    const junctionId = uuid();
    
    // Get or create route ID for the segment
    const routeId = activeLine.routeId || (() => {
        const newRouteId = uuid();
        activeLine.routeId = newRouteId;
        return newRouteId;
    })();

    // Assign route ID to current line
    a.line.routeId = routeId;

    // Determine which end of current line is being connected
    const isConnectingAtStart = a.line.start.x === 0 && a.line.start.y === 0;
    const currentLineJunction = isConnectingAtStart ? a.line.startJ : a.line.endJ;
    
    // Determine which end of the active line is being connected to
    const activeLineJunction = activeLine.startJ.active ? activeLine.startJ : activeLine.endJ;

    // Create shared junction point
    (currentLineJunction as any).id = junctionId;
    (activeLineJunction as any).id = junctionId;
}


/**
 * Функция возвращает массив линий с новым routeId, и с новыми startJ, endJ, но  парные joins должны совпадать
 */
function copyRoute(arrayOfLines: Line[]) {
    let newRoute: Line[] = [];

    let newRouteId = uuid();

    arrayOfLines.forEach(s => {
        let clone = s.getClone();   
        clone.routeId = newRouteId;
        newRoute.push(clone);
    })

    return newRoute;
}

/**
 * Функция возвращает соседнюю линию, присоединённую к началу start участка
 * @param {Line} selSeg - выбранный участок, в этой версии функции предполагается, что он один
 * @param {shapes[]} shapes - массив фигур, среди которых производится поиск
 */

function findConnectedLineStart(selSeg: Line, shapes: Shape[]) {
    var lines = shapes.filter(s => s.type === ObjectTypes.line).filter(s => (s as any).routeId === selSeg.routeId).filter(s => !s.isSelected);

    var startJID = selSeg.startJ.id;

    if (!startJID) return null;

    /**
     * мы не знаем каким концом присоединяется линия - start или end, 
     * поэтому проверим оба варианта
     */
    for (var i = 0; i < lines.length; i++) {
        if ((lines[i] as Line).endJ.id === startJID) {
            return lines[i];
        }
        else if ((lines[i] as Line).startJ.id === startJID) {
            return lines[i];
        }
    }

}

/**
 * Функция возвращает соседнюю линию, присоединённую к концу end участка
 * @param {Line} selSeg - выбранный участок, в этой версии функции предполагается, что он один
 * @param {shapes[]} shapes - массив фигур, среди которых производится поиск
 */

function findConnectedLineEnd(selSeg: Line, shapes: Shape[]) {

    var lines = shapes.filter(s => s.type === ObjectTypes.line).filter(s => (s as any).routeId === selSeg.routeId).filter(s => !s.isSelected);
    var endJID = selSeg.endJ.id;

    if (!endJID) return null;

    for (var i = 0; i < lines.length; i++) {
        if ((lines[i] as Line).endJ.id === endJID) {
            return lines[i];
        }
        else if ((lines[i] as Line).startJ.id === endJID) {
            return lines[i];
        }
    }
}

/**
 * Функция возвращает отводы, присоединённые к выбранному отрезку
 * @param selSeg выбранный отрезок
 * @param elbows коллекция отводов на чертеже
 */
export function findConnectedElbows(selSeg: Line) {
    return selSeg.getConnectedElbows();
}

function getPStartPEndProjWhenJoinedEnd(joinedEnd: Line, selectedSeg: Line, move_mat: mat3) {

    let vecJoinedEnd = new Vector(joinedEnd.start, joinedEnd.end);
    let vecJoinedEndParallel = vecJoinedEnd.createParallelVectorAtPoint(selectedSeg.start);


    let ps = transformPointByMatrix3(move_mat, selectedSeg.start);
    let pe = transformPointByMatrix3(move_mat, selectedSeg.end);

    let pointStart = projectPointToVector(ps, vecJoinedEndParallel);
    let pointEnd = projectPointToVector(pe, vecJoinedEnd);
    return { pointStart, pointEnd };
}

function getPStartPEndProjWhenJoinedStart(joinedStart: Line, selectedSeg: Line, move_mat: mat3) {
    let vecJoinedStart = new Vector(joinedStart.start, joinedStart.end);
    let vecJoinedStartParallel = vecJoinedStart.createParallelVectorAtPoint(selectedSeg.end);


    let ps = transformPointByMatrix3(move_mat, selectedSeg.start);
    let pe = transformPointByMatrix3(move_mat, selectedSeg.end);

    let pointStart = projectPointToVector(ps, vecJoinedStart);
    let pointEnd = projectPointToVector(pe, vecJoinedStartParallel);
    return { pointStart, pointEnd };
}

function getPStartPEndProjWhenJoinedBoth(joinedStart: Line, joinedEnd: Line, move_mat: mat3, selectedSeg: Line) {
    let vecJoinedStart = new Vector(joinedStart.start, joinedStart.end);
    let vecJoinedEnd = new Vector(joinedEnd.start, joinedEnd.end);

    let ps = transformPointByMatrix3(move_mat, selectedSeg.start);
    let pe = transformPointByMatrix3(move_mat, selectedSeg.end);

    let pointStart = projectPointToVector(ps, vecJoinedStart);
    let pointEnd = projectPointToVector(pe, vecJoinedEnd);
    return { pointStart, pointEnd };
}

function ifjoinedAtEnd(joinedStart: Line, seg: Line) {
    return joinedStart.endJ.id === seg.startJ.id || joinedStart.endJ.id === seg.endJ.id;
}

function ifjoinedAtStart(joinedStart: Line, seg: Line) {
    return joinedStart.startJ.id === seg.startJ.id || joinedStart.startJ.id === seg.endJ.id;
}




export { observeJoins, connect, copyRoute, findConnectedLineStart, findConnectedLineEnd, getPStartPEndProjWhenJoinedStart, getPStartPEndProjWhenJoinedEnd, getPStartPEndProjWhenJoinedBoth, ifjoinedAtEnd, ifjoinedAtStart }
